import { useCallback, useMemo } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import type { ImageProps } from '../../types';
import { calcCameraFovFromFocalLength, cameraEulerFromAbyssRotation } from '../Util';
import { useAbyssViewerContext } from '../AbyssViewerContext';

const WIDTH = 20;
const HEIGHT = 20;
const DEFAULT_MEAN_DISTANCE = 20;

export type useImageMeanDistanceProps = {
  image?: ImageProps;
  defaultMeanDistance?: number;
};

export const useImageMeanDistance = ({
  image,
  defaultMeanDistance = DEFAULT_MEAN_DISTANCE,
}: useImageMeanDistanceProps) => {
  const renderer = useThree((state) => state.gl);
  const { pickHelper } = useAbyssViewerContext();

  const temps = useMemo(
    () => ({
      renderTarget: new THREE.WebGLRenderTarget(WIDTH, HEIGHT),
      pixelBuffer: new Uint8Array(4 * WIDTH * HEIGHT),
      camera: new THREE.PerspectiveCamera(),
      pixelXYZ: new THREE.Vector3(),
    }),
    []
  );
  const getImageMeanDistance = useCallback(
    (image: ImageProps | undefined) => {
      if (!pickHelper) return undefined;
      if (!image) return undefined;
      const { renderTarget, pixelBuffer, camera, pixelXYZ } = temps;
      const { position, rotation, focalLength, height } = image;
      // position camera
      const euler = cameraEulerFromAbyssRotation(rotation);
      camera.position.set(...position);
      camera.rotation.copy(euler);
      camera.fov = calcCameraFovFromFocalLength(focalLength, height);
      camera.updateWorldMatrix(false, true);
      camera.updateProjectionMatrix();
      pickHelper.renderFrame(camera, renderer, renderTarget, pixelBuffer);

      let meanDistance = 0;
      let numPoints = 0;
      for (let index = 0; index < WIDTH * HEIGHT; index += 1) {
        const pointId = pickHelper.getPixelAsId(pixelBuffer, index);
        const getPointAttribute = pickHelper.getPointAttributeGetter(pointId);
        if (getPointAttribute) {
          const xyz = getPointAttribute('position');
          if (Array.isArray(xyz) && xyz.length === 3) {
            pixelXYZ.set(...xyz);
            meanDistance += pixelXYZ.distanceTo(camera.position);
            numPoints += 1;
          }
        }
      }
      if (numPoints === 0) {
        return undefined;
      }
      meanDistance /= numPoints;
      return meanDistance;
    },
    [temps, pickHelper, renderer]
  );

  const meanDistance = useMemo(
    () => image?.distance ?? getImageMeanDistance(image) ?? defaultMeanDistance,
    [getImageMeanDistance, image, defaultMeanDistance]
  );

  return { meanDistance };
};
